import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, MapPin, Calendar, Maximize, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

// ─── Project Data ─────────────────────────────────────────────────────────────
const pmcProjects = [
  {
    id: 'everest-chs',
    name: 'Everest CHS',
    type: 'Residential + Commercial',
    location: 'Santacruz West',
    area: '70,000 Sq.Ft',
    timeline: '42 Months',
    floors: 12,
    description: 'Everest CHS is an upcoming Residential + Commercial project in Santacruz East, located within the Airport Funnel Zone. The building has a top elevation of 12 floors as per regulations and features a 30:70 residential-to-commercial ratio, creating a well-planned mixed-use development.',
    images: [
      '/projects/everest/everest_1.png',
      '/projects/everest/everest_2.png',
      '/projects/everest/everest_3.png'
    ],
    category: 'pmc'
  },
  {
    id: 'sagar-apartments',
    name: 'Sagar Apartments',
    type: 'Uber Luxury Residential',
    location: 'Bandra West',
    area: '73,300 Sq.Ft',
    timeline: '36 Months',
    floors: 16,
    description: 'Sagar Apartments is an upcoming Uber Luxury Residential development in Bandra West, thoughtfully designed with a top elevation of 16 floors. Offering sea views and excellent connectivity to Carter Road and the upcoming Coastal Road, it promises an elevated living experience.',
    images: [
      '/projects/sagar/sagar_1.png',
      '/projects/sagar/sagar_3.jpeg'
    ],
    category: 'pmc'
  },
  {
    id: 'neelanjani-apartments',
    name: 'Neelanjani Apartments',
    type: 'Art Deco Residential',
    location: 'Vile Parle East',
    area: '17,500 Sq.Ft',
    timeline: '18 Months',
    floors: 8,
    description: 'Neelanjani Apartments is an upcoming Art Deco Residential building in Vile Parle East. Situated in the Airport funnel zone, this is a Self Redevelopment project guided and managed by Agastya Ventures.',
    images: [
      '/projects/neelanjani/neelanjani_1.jpeg',
      '/projects/neelanjani/neelanjani_2.png'
    ],
    category: 'pmc'
  },
  {
    id: 'dhanashree-heights',
    name: 'Dhanashree Heights',
    type: 'Residential + Commercial',
    location: 'Andheri West',
    area: '30,500 Sq.Ft',
    timeline: '30 Months',
    floors: 10,
    description: 'Dhanashree Heights is a mix-used Residential & Commercial Apartment in Azad Nagar, Andheri West. This project is a perfect balance of form and function.',
    images: [
      '/projects/dhanashree/dhanashree_2.jpeg',
      '/projects/dhanashree/dhanashree_1.png',
      '/projects/dhanashree/dhanashree_3.jpeg'
    ],
    category: 'pmc'
  }
];

const interiorProjects = [
  // ── Existing Projects ─────────────────────────────────────────────────────
  {
    id: 'sakar-swapna-chs',
    name: 'Sakar Swapna CHS',
    type: 'Interior Design',
    location: 'Andheri East',
    description: 'Sakar Swapna is a newly redeveloped building where we provide modern interior design services to its members. Our approach focuses on clean design, functional planning, and cost-friendly solutions, helping members create comfortable and contemporary homes without compromising on quality.',
    images: [
      '/projects/sakar-swapna/sakar1_2.png',
      '/projects/sakar-swapna/sakar1_3.jpeg',
      '/projects/sakar-swapna/sakar2_2.jpeg',
      '/projects/sakar-swapna/sakar2_3.jpeg',
      '/projects/sakar-swapna/sakar2_4.jpeg',
      '/projects/sakar-swapna/sakar2_5.jpeg'
    ],
    category: 'interior'
  },
  {
    id: 'mahavir-square',
    name: 'Mahavir Square',
    type: 'Private Residence',
    location: 'Thane',
    description: 'Private Residence at Mahavir Square, Thane, offering modern and classy interior solutions tailored to the client\'s lifestyle. The design focuses on elegant aesthetics, smart space planning, and refined finishes to create a comfortable and contemporary home.',
    images: [
      '/projects/mahavir/mahavir1_1.png',
      '/projects/mahavir/mahavir1_2.png',
      '/projects/mahavir/mahavir1_3.png',
      '/projects/mahavir/mahavir2_1.jpeg',
      '/projects/mahavir/mahavir2_3.jpeg',
      '/projects/mahavir/mahavir2_4.jpeg'
    ],
    category: 'interior'
  },
  {
    id: 'brisa-candolim',
    name: 'Brisa Candolim',
    type: 'Vacation Home',
    location: 'Candolim, Goa',
    description: 'Brisa is an apartment in Candolim, Goa, designed with elegant yet simple interiors that capture the relaxed Goa vibe while offering a luxurious and comfortable living experience.',
    images: [
      '/projects/brisa/brisa1_1.jpeg',
      '/projects/brisa/brisa1_2.jpeg',
      '/projects/brisa/brisa1_3.jpeg',
      '/projects/brisa/brisa2_1.jpeg',
      '/projects/brisa/brisa2_2.jpeg',
      '/projects/brisa/brisa2_3.jpeg'
    ],
    category: 'interior'
  },

  // ── New Projects from Portfolio PDF ──────────────────────────────────────
  {
    id: 'rovers-house',
    name: "Rover's House",
    type: 'Interior | Residential',
    location: 'Bandra',
    year: '2025',
    description: 'This design blends traditional craftsmanship with contemporary simplicity, creating a space that feels both warm and refined. A palette of neutral tones and rich wood elements establishes a grounded, timeless character, while carved furniture adds depth and a sense of heritage. Clean lines and uncluttered layouts balance these details, preventing the space from feeling heavy. The atmosphere is calm and intimate, enhanced by soft ambient lighting and subtle modern accents. The interplay of textures and materials brings quiet luxury without excess, resulting in a home that seamlessly bridges nostalgia and modern living.',
    images: [
      '/projects/rovers-house/rovers-house-bandra-interior-01.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-02.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-03.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-04.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-05.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-06.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-07.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-08.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-09.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-10.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-11.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-12.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-13.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-14.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-15.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-16.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-17.webp',
      '/projects/rovers-house/rovers-house-bandra-interior-18.webp',
    ],
    category: 'interior',
    metaTitle: "Rover's House - Luxury Interior Design in Bandra | Agastya Ventures",
    metaDescription: "Luxury residential interior at Rover's House, Bandra. A warm fusion of traditional craftsmanship and contemporary simplicity by Agastya Ventures."
  },
  {
    id: 'datta-niwas',
    name: 'Datta Niwas',
    type: 'Interior | Residential',
    location: 'Vile Parle East',
    year: '2026',
    description: 'This design reflects a soft, contemporary aesthetic rooted in minimalism and comfort. A neutral colour palette of whites, beiges, and muted tones creates a light and airy atmosphere, while clean lines and subtle detailing maintain a sense of visual clarity. The furniture is modern yet inviting, with plush forms and balanced compositions that enhance everyday livability. The spaces are thoughtfully layered with textures and gentle lighting, adding warmth without overwhelming the simplicity of the design. Carefully integrated elements, like panelled walls, sleek storage, and understated decor, bring quiet sophistication, resulting in a home that feels elegant, functional, and effortlessly cohesive.',
    images: [
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-01.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-02.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-03.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-04.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-05.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-06.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-07.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-08.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-09.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-10.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-11.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-12.webp',
      '/projects/datta-niwas/datta-niwas-vile-parle-east-interior-13.webp',
    ],
    category: 'interior',
    metaTitle: 'Datta Niwas - Contemporary Interior Design in Vile Parle East | Agastya Ventures',
    metaDescription: 'Modern minimalist residential interior at Datta Niwas, Vile Parle East. Clean lines, neutral tones, and quiet sophistication by Agastya Ventures.'
  },
  {
    id: 'searock-utopia',
    name: 'Searock Utopia',
    type: 'Architecture & Interiors | Resort',
    location: 'Dudhiware, Pawana',
    year: '2025',
    description: 'This design embraces a soft, modern aesthetic with subtle classical influences. Curved wall details, gentle arches, and a warm neutral palette create a sense of elegance and fluidity within the space. Natural light is maximised, enhancing the calm, airy atmosphere, while minimal yet thoughtfully selected furniture maintains visual clarity and comfort. The bedrooms and living areas reflect a balance of simplicity and character, with muted tones, clean lines, and light wood accents.',
    images: [
      '/projects/searock-utopia/searock-utopia-pawana-architecture-interior-01.webp',
      '/projects/searock-utopia/searock-utopia-pawana-architecture-interior-02.webp',
      '/projects/searock-utopia/searock-utopia-pawana-architecture-interior-03.webp',
      '/projects/searock-utopia/searock-utopia-pawana-architecture-interior-04.webp',
      '/projects/searock-utopia/searock-utopia-pawana-architecture-interior-05.webp',
      '/projects/searock-utopia/searock-utopia-pawana-architecture-interior-06.webp',
      '/projects/searock-utopia/searock-utopia-pawana-architecture-interior-07.webp',
    ],
    category: 'interior',
    metaTitle: 'Searock Utopia - Resort Architecture & Interiors in Pawana | Agastya Ventures',
    metaDescription: 'Elegant resort architecture and interiors at Searock Utopia, Dudhiware Pawana. Soft modern aesthetics with classical influences by Agastya Ventures.'
  },
  {
    id: 'sea-pearl',
    name: 'Sea Pearl',
    type: 'Interior | Residential',
    location: 'Charni Road',
    year: '2025',
    description: 'This design reflects a clean, contemporary aesthetic with a soft neutral palette that enhances openness and light. Minimal furniture and seamless materials create a sense of clarity, allowing the space to feel airy and well-balanced. The overall atmosphere is refined and calm, with subtle textures and warm lighting adding depth. It achieves a quiet sense of luxury while maintaining simplicity and functionality.',
    images: [
      '/projects/sea-pearl/sea-pearl-charni-road-interior-01.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-02.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-03.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-04.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-05.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-06.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-07.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-08.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-09.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-10.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-11.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-12.webp',
      '/projects/sea-pearl/sea-pearl-charni-road-interior-13.webp',
    ],
    category: 'interior',
    metaTitle: 'Sea Pearl - Contemporary Residential Interior in Charni Road | Agastya Ventures',
    metaDescription: 'Clean, contemporary residential interior at Sea Pearl, Charni Road. Neutral palette, minimal furniture, and quiet luxury by Agastya Ventures.'
  },
  {
    id: 'harnoli',
    name: 'Harnoli',
    type: 'Interior | Residential',
    location: 'Pali',
    year: '2024',
    description: 'This design brings together a warm, contemporary aesthetic with subtle traditional undertones. Earthy tones, natural wood finishes, and soft textures create a cozy yet refined atmosphere, while clean lines keep the spaces visually balanced. The furniture and detailing feel grounded and functional, with just enough character to add depth. The overall mood is intimate and inviting, enhanced by warm lighting and thoughtful material layering. It strikes a balance between comfort and sophistication, resulting in a space that feels lived-in, calm, and quietly elegant.',
    images: [
      '/projects/harnoli/harnoli-pali-interior-01.webp',
      '/projects/harnoli/harnoli-pali-interior-02.webp',
      '/projects/harnoli/harnoli-pali-interior-03.webp',
      '/projects/harnoli/harnoli-pali-interior-04.webp',
      '/projects/harnoli/harnoli-pali-interior-05.webp',
      '/projects/harnoli/harnoli-pali-interior-06.webp',
    ],
    category: 'interior',
    metaTitle: 'Harnoli - Warm Contemporary Interior Design in Pali | Agastya Ventures',
    metaDescription: 'Warm, contemporary residential interior at Harnoli, Pali. Earthy tones, natural wood, and refined comfort by Agastya Ventures.'
  },
  {
    id: 'ichimi-restaurant-lobby',
    name: 'Ichimi Restaurant Lobby',
    type: 'Interior | Reception',
    location: 'Sambhaji Nagar',
    year: '2026',
    description: 'This design draws inspiration from traditional Japanese architecture, emphasizing simplicity, natural materials, and a strong connection to craftsmanship. Exposed wooden structures, warm tones, and clean geometries create a grounded and harmonious environment. The spatial layout feels open yet intimate, reflecting a sense of balance and order. The atmosphere is calm and contemplative, enhanced by soft lighting and minimal detailing. The use of wood, neutral hues, and structured forms brings a timeless quality, resulting in a space that feels serene, authentic, and deeply rooted in tradition.',
    images: [
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-01.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-02.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-03.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-04.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-05.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-06.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-07.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-08.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-09.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-10.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-11.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-12.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-13.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-14.webp',
      '/projects/ichimi-restaurant-lobby/ichimi-restaurant-lobby-sambhaji-nagar-interior-15.webp',
    ],
    category: 'interior',
    metaTitle: 'Ichimi Restaurant Lobby - Japanese-Inspired Interior in Sambhaji Nagar | Agastya Ventures',
    metaDescription: 'Japanese-inspired restaurant lobby at Ichimi, Sambhaji Nagar. Natural materials, warm tones, and serene craftsmanship by Agastya Ventures.'
  },
  {
    id: 'ichimi-japanese-restaurant',
    name: 'Ichimi Japanese Restaurant',
    type: 'Interior | Restaurant',
    location: 'Sambhaji Nagar',
    year: '2026',
    description: 'This design draws inspiration from traditional Japanese architecture, emphasizing simplicity, natural materials, and a strong connection to craftsmanship. Exposed wooden structures, warm tones, and clean geometries create a grounded and harmonious environment. The spatial layout feels open yet intimate, reflecting a sense of balance and order. The atmosphere is calm and contemplative, enhanced by soft lighting and minimal detailing. The use of wood, neutral hues, and structured forms brings a timeless quality, resulting in a space that feels serene, authentic, and deeply rooted in tradition.',
    images: [
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-01.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-02.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-03.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-04.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-05.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-06.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-07.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-08.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-09.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-10.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-11.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-12.webp',
      '/projects/ichimi-japanese-restaurant/ichimi-japanese-restaurant-sambhaji-nagar-interior-13.webp',
    ],
    category: 'interior',
    metaTitle: 'Ichimi Japanese Restaurant - Traditional Japanese Interior in Sambhaji Nagar | Agastya Ventures',
    metaDescription: 'Authentic Japanese restaurant interior at Ichimi, Sambhaji Nagar. Serene, traditional craftsmanship and warm wood tones by Agastya Ventures.'
  },
  {
    id: 'ichimi-hotel-lobby',
    name: 'Ichimi Hotel Lobby',
    type: 'Interior | Reception',
    location: 'Sambhaji Nagar',
    year: '2026',
    description: 'This design reflects a refined, hospitality-driven aesthetic inspired by Japanese minimalism. Soft neutral tones, delicate wall art, and clean-lined furniture create a welcoming yet elegant atmosphere. The use of natural materials and subtle textures adds warmth while maintaining visual simplicity. The spaces feel calm, curated, and thoughtfully detailed, with a focus on comfort and understated luxury. Balanced lighting and minimal ornamentation enhance the serene experience, resulting in an environment that feels both contemporary and culturally rooted.',
    images: [
      '/projects/ichimi-hotel-lobby/ichimi-hotel-lobby-sambhaji-nagar-interior-02.webp',
      '/projects/ichimi-hotel-lobby/ichimi-hotel-lobby-sambhaji-nagar-interior-03.webp',
      '/projects/ichimi-hotel-lobby/ichimi-hotel-lobby-sambhaji-nagar-interior-04.webp',
      '/projects/ichimi-hotel-lobby/ichimi-hotel-lobby-sambhaji-nagar-interior-05.webp',
      '/projects/ichimi-hotel-lobby/ichimi-hotel-lobby-sambhaji-nagar-interior-06.webp',
    ],
    category: 'interior',
    metaTitle: 'Ichimi Hotel Lobby - Japanese Minimalist Hospitality Interior | Agastya Ventures',
    metaDescription: 'Japanese minimalist hotel lobby at Ichimi, Sambhaji Nagar. Curated, calm, and culturally rooted hospitality design by Agastya Ventures.'
  },
];
const allProjects = [...pmcProjects, ...interiorProjects];

// Image Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white z-50"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>
      
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-50"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-50"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
        </>
      )}
      
      <motion.img
        key={currentIndex}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        src={images[currentIndex]}
        alt="Project"
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

// Portfolio Hero
const PortfolioHero = () => {
  return (
    <section className="pt-[140px] pb-[120px] md:pt-[180px] bg-background" data-testid="portfolio-hero">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">Our Portfolio</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Projects That<br />
            <span className="text-gold">Define Excellence</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Explore our curated collection of PMC projects and interior design work. 
            Each project reflects our commitment to quality, innovation, and client satisfaction.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Project Card Component
const ProjectCard = ({ project, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative aspect-[4/5] overflow-hidden project-card border border-white/5"
      data-testid={`project-card-${project.id}`}
    >
      {/* Thumbnail image with hover scale */}
      <img
        src={project.images[0]}
        alt={`Luxury interior design - ${project.name} by Agastya Ventures`}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[400ms] ease-out group-hover:scale-[1.05]"
      />

      {/* Gradient overlay — darkens on hover for better text contrast */}
      <div
        className="absolute inset-0 transition-opacity duration-[400ms] ease-out"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.1) 70%, transparent 100%)',
        }}
      />
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[400ms] ease-out"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.60) 50%, rgba(0,0,0,0.20) 100%)',
        }}
      />

      {/* Text content */}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 z-10">
        {/* Category / type */}
        <p className="text-xs uppercase tracking-widest text-gold mb-1.5 transition-all duration-[400ms] group-hover:text-gold/100"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)', color: 'rgba(212,175,55,0.85)' }}>
          {project.type}
        </p>

        {/* Project name */}
        <h3
          className="text-xl md:text-2xl font-serif mb-2 transition-all duration-[400ms] group-hover:brightness-110 leading-tight"
          style={{
            color: '#FFFFFF',
            fontWeight: 650,
            letterSpacing: '-0.01em',
            textShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          {project.name}
        </h3>

        {/* Location + optional year */}
        <div className="flex items-center gap-3 text-sm transition-all duration-[400ms] group-hover:brightness-110"
          style={{ color: 'rgba(255,255,255,0.85)', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {project.location}
          </span>
          {project.year && (
            <>
              <span className="opacity-60">·</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {project.year}
              </span>
            </>
          )}
          {project.area && (
            <>
              <span className="opacity-60">·</span>
              <span className="flex items-center gap-1">
                <Maximize className="w-3 h-3" />
                {project.area}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Full-card click link */}
      <Link
        to={`/portfolio/${project.id}`}
        className="absolute inset-0 z-20"
        aria-label={`View ${project.name} - ${project.type} in ${project.location}`}
      />
    </motion.div>
  );
};

// PMC Projects Section
const PMCProjects = () => {
  return (
    <section id="pmc" className="arch-section bg-card" data-testid="pmc-projects-section">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">Project Management</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">
            PMC Projects
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pmcProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Interior Projects Section
const InteriorProjects = () => {
  return (
    <section id="interior" className="arch-section bg-background" data-testid="interior-projects-section">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">Interior Design</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">
            Interior Projects
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {interiorProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const PortfolioCTA = () => {
  return (
    <section className="arch-section bg-card" data-testid="portfolio-cta">
      <div className="arch-container text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
            Have a Project in Mind?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Let's discuss how we can transform your real estate vision into a remarkable reality.
          </p>
          <Link to="/contact">
            <Button
              className="bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
              data-testid="portfolio-cta-btn"
            >
              Start Your Project
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Main Portfolio Page
const PortfolioPage = () => {
  return (
    <main data-testid="portfolio-page">
      <PortfolioHero />
      <PMCProjects />
      <InteriorProjects />
      <PortfolioCTA />
    </main>
  );
};

// Project Detail Page
export const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const project = allProjects.find(p => p.id === projectId);
  
  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-serif text-white mb-4">Project Not Found</h1>
          <Link to="/portfolio">
            <Button variant="outline" className="border-white/20 text-white">
              Back to Portfolio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  return (
    <main data-testid="project-detail-page">
      {/* Hero */}
      <section className="pt-[140px] pb-[60px] md:pt-[180px] bg-background">
        <div className="arch-container">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold transition-colors mb-8">
            <ChevronLeft className="w-4 h-4" />
            Back to Portfolio
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest text-gold mb-4">{project.type}</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6">
              {project.name}
            </h1>
            
            <div className="flex flex-wrap gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" />
                {project.location}
              </div>
              {project.year && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gold" />
                  {project.year}
                </div>
              )}
              {project.area && (
                <div className="flex items-center gap-2">
                  <Maximize className="w-4 h-4 text-gold" />
                  {project.area}
                </div>
              )}
              {project.timeline && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gold" />
                  {project.timeline}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Image */}
      <section className="pb-8 bg-background">
        <div className="arch-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative aspect-[16/9] cursor-pointer group"
            onClick={() => openLightbox(0)}
          >
            <img
              src={project.images[0]}
              alt={`${project.type} - ${project.name} in ${project.location} by Agastya Ventures`}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm uppercase tracking-widest">
                Click to Enlarge
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Description & Gallery */}
      <section className="arch-section bg-card">
        <div className="arch-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl font-serif text-white mb-6">About the Project</h2>
              <p className="text-muted-foreground leading-relaxed text-lg mb-8">
                {project.description}
              </p>
              
              {project.floors && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border border-white/10">
                    <p className="text-2xl font-serif text-gold">{project.floors}</p>
                    <p className="text-sm text-muted-foreground">Floors</p>
                  </div>
                  <div className="p-4 border border-white/10">
                    <p className="text-2xl font-serif text-gold">{project.area}</p>
                    <p className="text-sm text-muted-foreground">Construction Area</p>
                  </div>
                </div>
              )}
            </motion.div>
            
            {project.images.length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="text-2xl font-serif text-white mb-6">Gallery</h2>
                <div className="grid grid-cols-2 gap-4">
                  {project.images.slice(1).map((image, index) => (
                    <div
                      key={index}
                      className="aspect-square cursor-pointer group relative overflow-hidden"
                      onClick={() => openLightbox(index + 1)}
                    >
                      <img
                        src={image}
                        alt={`${project.name} - ${index + 2}`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.02]"
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="arch-section bg-background">
        <div className="arch-container text-center">
          <h2 className="text-3xl font-serif text-white mb-6">
            Interested in a Similar Project?
          </h2>
          <Link to="/contact">
            <Button
              className="bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
            >
              Get in Touch
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={project.images}
            currentIndex={currentImageIndex}
            onClose={() => setLightboxOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
          />
        )}
      </AnimatePresence>
    </main>
  );
};

export default PortfolioPage;
